import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useBranch } from "@/context/BranchContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { fmtGHS, fmtNum, fmtDateTime } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Package, Boxes, Banknote, ShoppingCart, AlertTriangle, CalendarClock,
  XCircle, ArrowLeftRight, FileText, Building2,
} from "lucide-react";

function Kpi({ icon: Icon, label, value, tone = "slate", sub }) {
  const tones = {
    slate: "bg-muted text-muted-foreground",
    sky: "bg-sky-100 text-sky-600 dark:bg-sky-500/15 dark:text-sky-300",
    emerald: "bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300",
    amber: "bg-amber-100 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300",
    rose: "bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300",
    violet: "bg-violet-100 text-violet-600 dark:bg-violet-500/15 dark:text-violet-300",
  };
  return (
    <Card className="p-4 shadow-sm border-border">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <span className={`h-8 w-8 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </span>
      </div>
      <div className="mt-2 text-2xl font-bold font-display text-foreground tabular">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </Card>
  );
}

export default function Dashboard() {
  const { branchParam, activeBranch, branches } = useBranch();
  const { can } = useAuth();
  const showRecentActivity = can("dashboard.activity");
  const showSalesByBranch = can("dashboard.sales");
  const showStock = can("dashboard.stock");
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard", { params: { branch_id: branchParam } }).then((r) => setData(r.data)).catch(() => {});
  }, [branchParam]);

  if (!data) return <div className="text-muted-foreground">Loading dashboard…</div>;
  const k = data.kpis;
  const scope = activeBranch === "all" ? "Network-wide" : branches.find((b) => b.id === activeBranch)?.name;

  return (
    <div>
      <PageHeader title="Operations Dashboard" subtitle={`${scope} · live inventory & sales overview`} />

      {(showStock || showSalesByBranch) && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-testid="dashboard-kpis">
        {showStock && <Kpi icon={Banknote} label="Stock Value (cost)" value={fmtGHS(k.stock_value)} tone="sky" sub={`Retail ${fmtGHS(k.retail_value)}`} />}
        {showSalesByBranch && <Kpi icon={ShoppingCart} label="Today's Sales" value={fmtGHS(k.today_sales)} tone="emerald" sub={`${fmtNum(k.order_count)} orders total`} />}
        {showStock && <Kpi icon={Package} label="Products" value={fmtNum(k.product_count)} tone="slate" sub={`${k.branch_count} locations`} />}
        {showSalesByBranch && <Kpi icon={Banknote} label="Total Sales" value={fmtGHS(k.total_sales)} tone="violet" />}
        {showStock && <>
          <Kpi icon={AlertTriangle} label="Low / Out of Stock" value={fmtNum(k.low_stock_count)} tone="amber" />
          <Kpi icon={CalendarClock} label="Near Expiry" value={fmtNum(k.near_expiry_count)} tone="amber" />
          <Kpi icon={XCircle} label="Expired Batches" value={fmtNum(k.expired_count)} tone="rose" />
          <Kpi icon={ArrowLeftRight} label="Pending Transfers" value={fmtNum(k.pending_transfers)} tone="slate" sub={`${k.pending_claims} claims pending`} />
        </>}
      </div>
      )}
      {!showStock && !showSalesByBranch && !showRecentActivity && (
        <div className="text-sm text-muted-foreground" data-testid="dashboard-empty">No dashboard cards are enabled for your account.</div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {showSalesByBranch && (
        <Card className={`p-5 shadow-sm ${showRecentActivity ? "lg:col-span-2" : "lg:col-span-3"}`}>
          <h3 className="font-display font-semibold text-foreground mb-4 flex items-center gap-2">
            <Building2 className="h-4 w-4 text-muted-foreground" /> Sales by Branch
          </h3>
          {data.sales_by_branch.length === 0 ? (
            <div className="h-56 flex items-center justify-center text-sm text-muted-foreground">No sales recorded yet</div>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.sales_by_branch}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="branch" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip formatter={(v) => fmtGHS(v)} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="sales" fill="#0284c7" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        )}

        {showRecentActivity && (
        <Card className={`p-5 shadow-sm ${showSalesByBranch ? "" : "lg:col-span-3"}`}>
          <h3 className="font-display font-semibold text-foreground mb-4 flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" /> Recent Activity
          </h3>
          <div className="space-y-3">
            {data.recent_audit.length === 0 && <div className="text-sm text-muted-foreground">No activity yet</div>}
            {data.recent_audit.map((a) => (
              <div key={a.id} className="text-xs border-l-2 border-sky-200 pl-3">
                <div className="font-medium text-foreground">{a.action.replace(/_/g, " ")}</div>
                <div className="text-muted-foreground">{a.description || a.entity_type}</div>
                <div className="text-muted-foreground mt-0.5">{a.actor_name} · {fmtDateTime(a.timestamp)}</div>
              </div>
            ))}
          </div>
        </Card>
        )}
      </div>

      {showSalesByBranch && (
      <Card className="p-5 shadow-sm mt-6" data-testid="dashboard-recent-sales">
        <h3 className="font-display font-semibold text-foreground mb-4">Recent Sales</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="py-2 pr-3">Invoice</th><th className="py-2 pr-3">Customer</th>
                <th className="py-2 pr-3">Type</th><th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3 text-right">Total</th><th className="py-2 pr-3">When</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_orders.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">No sales yet</td></tr>
              )}
              {data.recent_orders.map((o) => (
                <tr key={o.id} className="border-b border-border hover:bg-muted/50">
                  <td className="py-2 pr-3 font-mono text-xs">{o.invoice_no}</td>
                  <td className="py-2 pr-3">{o.customer_name}</td>
                  <td className="py-2 pr-3"><StatusBadge value={o.customer_type} /></td>
                  <td className="py-2 pr-3"><StatusBadge value={o.payment_status} label={o.payment_status.replace(/_/g, " ")} /></td>
                  <td className="py-2 pr-3 text-right font-mono">{fmtGHS(o.total)}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{fmtDateTime(o.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      )}
    </div>
  );
}
